import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
    type Dispatch,
    type SetStateAction,
} from 'react';
import {
    GeoapifyContext,
    GeoapifyGeocoderAutocomplete,
} from '@geoapify/react-geocoder-autocomplete';
import '@geoapify/geocoder-autocomplete/styles/minimal.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import type { DeliveryAddressPayload } from '../services/payment.service';

// Build country options once (memoised at module level)
const COUNTRY_OPTIONS = countryList().getData() as { value: string; label: string }[];

const GEOAPIFY_API_KEY = import.meta.env.VITE_GEOAPIFY_API_KEY ?? '';
const FILTER_ES = ['es'] as ['es'];

type GeoapifyPlace = {
    properties?: {
        address_line1?: string;
        street?: string;
        name?: string;
        housenumber?: string;
        city?: string;
        postcode?: string;
        country?: string;
        formatted?: string;
        state?: string;
        country_code?: string;
        county?: string;
    };
};

/** Street + house number only, space-separated (no comma). */
function streetAndNumber(properties: GeoapifyPlace['properties']): string {
    if (!properties) return '';
    const street = String(properties.street || properties.name || '').trim();
    const number = String(properties.housenumber || '').trim();
    const line = [street, number].filter(Boolean).join(' ');
    if (line) return line;
    // Fallback: strip commas from address_line1 if structured fields are missing
    return String(properties.address_line1 || '')
        .replace(/,\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export type DeliveryOption = 'individual' | 'hybrid' | 'grouped';
export type DeliveryChoice = 'individual' | 'grouped';

/**
 * Provinces/territories that are not reachable via individual shipping.
 * The "Provincia" field is free text, so both the Spanish and English
 * spellings are listed
 */
export const CUSTOMS_PROVINCES = [
    'canary islands',
    'canarias',
    'ceuta',
    'melilla',
    'balearic islands',
    'islas baleares',
];

function normalizeProvince(value: string): string {
    return value.trim().toLowerCase();
}

function isCustomsProvince(value: string): boolean {
    return CUSTOMS_PROVINCES.includes(normalizeProvince(value));
}

const INDIVIDUAL_SHIPPING_COUNTRY_ERROR =
    'Los envíos individuales solo están disponibles dentro de España.';
const INDIVIDUAL_SHIPPING_PROVINCE_ERROR =
    'No es posible realizar envíos individuales a esta provincia.';

export interface DeliveryAddressForm {
    recepient_name: string;
    recepient_email: string;
    recepient_phone: string;
    recepient_line_1: string;
    recepient_line_2?: string;
    recepient_city: string;
    recepient_postal_code: string;
    recepient_province: string;
    recepient_country: string;
}

export const EMPTY_DELIVERY_ADDRESS: DeliveryAddressForm = {
    recepient_name: '',
    recepient_email: '',
    recepient_phone: '',
    recepient_line_1: '',
    recepient_line_2: undefined,
    recepient_city: '',
    recepient_postal_code: '',
    recepient_province: '',
    recepient_country: '',
};

/** Address row as stored on the group (Supabase / future catalog). */
export interface GroupDeliveryAddress {
    id?: string;
    owner_type?: 'group' | 'group_order' | 'manual';
    recipient_name?: string;
    recipient_email?: string;
    recipient_phone?: string;
    recipient_line_1?: string;
    recipient_line_2?: string;
    recipient_city?: string;
    recipient_postal_code?: string;
    recipient_province?: string;
    recipient_country?: string;
    recepient_name?: string;
    recepient_email?: string;
    recepient_phone?: string;
    recepient_line_1?: string;
    recepient_line_2?: string;
    recepient_city?: string;
    recepient_postal_code?: string;
    recepient_province?: string;
    recepient_country?: string;
}

export function pickGroupDeliveryAddress(
    addresses: GroupDeliveryAddress[] | undefined | null
): GroupDeliveryAddress | undefined {
    if (!addresses?.length) return undefined;
    return addresses.find((a) => a.owner_type === 'group') ?? undefined;
}

export function mapGroupAddressToForm(
    address: GroupDeliveryAddress | undefined | null
): DeliveryAddressForm {
    if (!address) return { ...EMPTY_DELIVERY_ADDRESS };
    return {
        recepient_name: address.recipient_name ?? address.recepient_name ?? '',
        recepient_email: address.recipient_email ?? address.recepient_email ?? '',
        recepient_phone: address.recipient_phone ?? address.recepient_phone ?? '',
        recepient_line_1: address.recipient_line_1 ?? address.recepient_line_1 ?? '',
        recepient_line_2: address.recipient_line_2 ?? address.recepient_line_2 ?? '',
        recepient_city: address.recipient_city ?? address.recepient_city ?? '',
        recepient_postal_code: address.recipient_postal_code ?? address.recepient_postal_code ?? '',
        recepient_province: address.recipient_province ?? address.recepient_province ?? '',
        recepient_country: address.recipient_country ?? address.recepient_country ?? '',
    };
}

/** Payload matching public.delivery_addresses / CreatePaymentRequest.delivery_address. */
export function toDeliveryAddressPayload(
    address: DeliveryAddressForm,
    ownerType: DeliveryAddressPayload['owner_type'] = 'group_order'
): DeliveryAddressPayload {
    return {
        owner_type: ownerType,
        recipient_name: address.recepient_name,
        recipient_email: address.recepient_email,
        recipient_phone: address.recepient_phone,
        recipient_line_1: address.recepient_line_1,
        recipient_line_2: address.recepient_line_2 || null,
        recipient_city: address.recepient_city,
        recipient_postal_code: address.recepient_postal_code,
        recipient_province: address.recepient_province,
        recipient_country: address.recepient_country,
    };
}

export type DeliveryAddressErrors = Partial<Record<keyof DeliveryAddressForm, string>>;

export interface DeliveryAddressFormHandle {
    validate: () => boolean;
}

interface DeliveryAddressFormProps {
    deliveryAddress: DeliveryAddressForm;
    setDeliveryAddress: Dispatch<SetStateAction<DeliveryAddressForm>>;
    highlightFields?: Array<keyof DeliveryAddressForm>;
    onClearHighlight?: (field: keyof DeliveryAddressForm) => void;
    restrictToSpainIndividual?: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type RequiredDeliveryAddressField = Exclude<keyof DeliveryAddressForm, 'recepient_line_2'>;

const REQUIRED_MESSAGES: Record<RequiredDeliveryAddressField, string> = {
    recepient_name: 'El nombre del receptor es requerido',
    recepient_email: 'El email es requerido',
    recepient_phone: 'El teléfono es requerido',
    recepient_line_1: 'La línea 1 de la dirección es requerida',
    recepient_city: 'La ciudad es requerida',
    recepient_postal_code: 'El código postal es requerido',
    recepient_province: 'La provincia es requerida',
    recepient_country: 'El país es requerido',
};

export interface ValidateDeliveryAddressOptions {
    /**
     * Individual shipments are currently only available within mainland
     * Spain. When true, any country other than Spain and any province in
     * `CUSTOMS_PROVINCES` (Canary Islands, Balearic Islands, Ceuta, Melilla)
     * is rejected. Never set this for group/organizer addresses — a group
     * can ship anywhere.
     */
    restrictToSpainIndividual?: boolean;
}

export function validateDeliveryAddress(
    address: DeliveryAddressForm,
    options?: ValidateDeliveryAddressOptions
): DeliveryAddressErrors {
    const errors: DeliveryAddressErrors = {};

    (Object.keys(REQUIRED_MESSAGES) as RequiredDeliveryAddressField[]).forEach((field) => {
        if (!address[field].trim()) {
            errors[field] = REQUIRED_MESSAGES[field];
        }
    });

    if (!errors.recepient_email && !EMAIL_REGEX.test(address.recepient_email.trim())) {
        errors.recepient_email = 'Email inválido';
    }

    const phoneDigits = address.recepient_phone.replace(/\D/g, '');
    if (!errors.recepient_phone && phoneDigits.length < 9) {
        errors.recepient_phone = 'Número de teléfono inválido';
    }

    if (options?.restrictToSpainIndividual) {
        if (!errors.recepient_country && address.recepient_country.trim().toUpperCase() !== 'ES') {
            errors.recepient_country = INDIVIDUAL_SHIPPING_COUNTRY_ERROR;
        }
        if (!errors.recepient_province && isCustomsProvince(address.recepient_province)) {
            errors.recepient_province = INDIVIDUAL_SHIPPING_PROVINCE_ERROR;
        }
    }

    return errors;
}

function Field({
    name,
    type = 'text',
    value,
    error,
    highlight,
    label,
    placeholder,
    className,
    onChange,
}: {
    name: keyof DeliveryAddressForm;
    type?: string;
    value: string;
    error?: string;
    highlight?: boolean;
    label: string;
    placeholder: string;
    className?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
    const marked = !!error || !!highlight;
    return (
        <div className={`space-y-1.5 ${className ?? ''}`}>
            <label htmlFor={name} className={`block text-sm italic ${marked ? 'text-red-600' : 'text-gray-700'}`}>
                {label}
            </label>
            <input
                id={name}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                aria-invalid={marked}
                aria-describedby={error ? `${name}-error` : undefined}
                className={`w-full border ${marked ? 'border-red-500' : 'border-black'} rounded-none h-10 px-3 ${
                    marked ? 'border-red-500' : 'border-black'
                }`}
            />
            {error && (
                <p id={`${name}-error`} className="text-red-500 text-xs mt-1">
                    {error}
                </p>
            )}
        </div>
    );
}

const DeliveryAddressForm = forwardRef<DeliveryAddressFormHandle, DeliveryAddressFormProps>(
    function DeliveryAddressForm(
        { deliveryAddress, setDeliveryAddress, highlightFields, onClearHighlight, restrictToSpainIndividual },
        ref
    ) {
        const [errors, setErrors] = useState<DeliveryAddressErrors>({});

        useImperativeHandle(ref, () => ({
            validate: () => {
                const nextErrors = validateDeliveryAddress(deliveryAddress, { restrictToSpainIndividual });
                setErrors(nextErrors);
                return Object.keys(nextErrors).length === 0;
            },
        }));

        // Individual shipments are locked to Spain — auto-correct the country
        // if it's ever out of sync (e.g. a previously saved non-ES address).
        // Use a functional update so we never clobber a parent that just hydrated
        // the form (stale empty spread was wiping fields on first modal open).
        useEffect(() => {
            if (!restrictToSpainIndividual) return;
            if (deliveryAddress.recepient_country === 'ES') return;
            setDeliveryAddress((prev) =>
                prev.recepient_country === 'ES' ? prev : { ...prev, recepient_country: 'ES' }
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [restrictToSpainIndividual, deliveryAddress.recepient_country]);

        const clearFieldError = (field: keyof DeliveryAddressForm) => {
            onClearHighlight?.(field);
            if (errors[field]) {
                setErrors((prev) => ({ ...prev, [field]: undefined }));
            }
        };

        // Library-owned input: set id + close suggestions on focus loss
        // (Geoapify only closes the list on document click, not on Tab/blur)
        useEffect(() => {
            const container = document.querySelector<HTMLElement>(
                '#delivery-address-form .geocoder-container'
            );
            const input = container?.querySelector<HTMLInputElement>('.geoapify-autocomplete-input');
            if (!input || !container) return;

            input.id = 'recepient_line_1';
            input.name = 'recepient_line_1';

            const onBlur = () => {
                window.setTimeout(() => {
                    if (document.activeElement === input) return;
                    container.querySelector('.geoapify-autocomplete-items')?.remove();
                }, 150);
            };

            input.addEventListener('blur', onBlur);
            return () => input.removeEventListener('blur', onBlur);
        }, []);

        const handlePhoneChange = (value: string, data: { countryCode?: string }) => {
            const phone = value.startsWith('+') ? value : `+${value}`;
            // Sync recipient country with the flag the user picked in the phone input.
            // Skip this for individual shipments, whose country is locked to Spain.
            const phoneCountry = restrictToSpainIndividual
                ? ''
                : data?.countryCode?.toUpperCase() ?? '';
            setDeliveryAddress({
                ...deliveryAddress,
                recepient_phone: phone,
                ...(phoneCountry ? { recepient_country: phoneCountry } : {}),
            });
            onClearHighlight?.('recepient_phone');
            if (phoneCountry) onClearHighlight?.('recepient_country');
            if (errors.recepient_phone) {
                setErrors((prev) => ({ ...prev, recepient_phone: undefined }));
            }
            if (phoneCountry && errors.recepient_country) {
                setErrors((prev) => ({ ...prev, recepient_country: undefined }));
            }
        };

        const phoneMarked = !!errors.recepient_phone || !!highlightFields?.includes('recepient_phone');
        const line1Marked =
            !!errors.recepient_line_1 || !!highlightFields?.includes('recepient_line_1');

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            const field = name as keyof DeliveryAddressForm;
            setDeliveryAddress({ ...deliveryAddress, [field]: value });
            clearFieldError(field);

            if (restrictToSpainIndividual && field === 'recepient_province' && isCustomsProvince(value)) {
                setErrors((prev) => ({ ...prev, recepient_province: INDIVIDUAL_SHIPPING_PROVINCE_ERROR }));
            }
        };

        function handlePlaceSelect(place: GeoapifyPlace) {
            const p = place?.properties;
            if (!p) return;

            const streetLine = streetAndNumber(p);
            // Library writes the full formatted address first; replace immediately
            const input = document.querySelector<HTMLInputElement>('#recepient_line_1');
            if (input) input.value = streetLine;

            clearFieldError('recepient_line_1');
            const nextProvince = p.county ?? '';
            setDeliveryAddress({
                ...deliveryAddress,
                recepient_line_1: streetLine,
                recepient_city: p.city ?? '',
                recepient_postal_code: p.postcode ?? '',
                recepient_province: nextProvince,
                recepient_country: restrictToSpainIndividual ? 'ES' : p.country_code?.toUpperCase() ?? '',
            });

            if (restrictToSpainIndividual && isCustomsProvince(nextProvince)) {
                setErrors((prev) => ({ ...prev, recepient_province: INDIVIDUAL_SHIPPING_PROVINCE_ERROR }));
            } else {
                clearFieldError('recepient_province');
            }
        }

        return (
            <div id="delivery-address-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                    name="recepient_name"
                    label="Nombre del destinatario"
                    value={deliveryAddress.recepient_name}
                    error={errors.recepient_name}
                    placeholder="Nombre completo"
                    onChange={handleChange}
                />
                <Field
                    name="recepient_email"
                    type="email"
                    label="Email"
                    value={deliveryAddress.recepient_email}
                    error={errors.recepient_email}
                    placeholder="correo@ejemplo.com"
                    onChange={handleChange}
                />
                <div className="space-y-1.5 md:col-span-2">
                    <label
                        htmlFor="recepient_phone"
                        className={`block text-sm font-medium ${phoneMarked ? 'text-red-600' : 'text-gray-700'}`}
                    >
                        Teléfono
                    </label>
                    <div className="phone-input-container">
                        <PhoneInput
                            country={'es'}
                            value={deliveryAddress.recepient_phone}
                            onChange={handlePhoneChange}
                            containerClass={phoneMarked ? 'react-tel-input error' : 'react-tel-input'}
                            enableSearch
                            autoFormat
                            countryCodeEditable={false}
                            specialLabel=""
                            searchPlaceholder="Buscar país..."
                            searchNotFound="País no encontrado"
                            preferredCountries={['es', 'it', 'fr', 'pt', 'de', 'gb']}
                            inputProps={{
                                id: 'recepient_phone',
                                name: 'recepient_phone',
                                required: true,
                                placeholder: 'Número de teléfono',
                                'aria-invalid': phoneMarked,
                                'aria-describedby': errors.recepient_phone
                                    ? 'recepient_phone-error'
                                    : undefined,
                            }}
                        />
                    </div>
                    {errors.recepient_phone && (
                        <p id="recepient_phone-error" className="text-red-500 text-xs mt-1">
                            {errors.recepient_phone}
                        </p>
                    )}
                </div>
                <div className={`space-y-1.5 md:col-span-2 ${line1Marked ? 'geoapify-field-error' : ''}`}>
                    <label
                        htmlFor="recepient_line_1"
                        className={`block text-sm italic ${line1Marked ? 'text-red-600' : 'text-gray-700'}`}
                    >
                        Dirección línea 1
                    </label>
                    <GeoapifyContext apiKey={GEOAPIFY_API_KEY}>
                        <GeoapifyGeocoderAutocomplete
                            value={deliveryAddress.recepient_line_1}
                            placeholder="Calle, número, piso..."
                            filterByCountryCode={FILTER_ES}
                            lang="es"
                            placeSelect={handlePlaceSelect}
                            onUserInput={(value: string) => {
                                setDeliveryAddress({ ...deliveryAddress, recepient_line_1: value });
                                clearFieldError('recepient_line_1');
                            }}
                        />
                    </GeoapifyContext>
                    {errors.recepient_line_1 && (
                        <p id="recepient_line_1-error" className="text-red-500 text-xs mt-1">
                            {errors.recepient_line_1}
                        </p>
                    )}
                </div>
                <Field
                    name="recepient_line_2"
                    className="md:col-span-2"
                    label="Dirección línea 2"
                    value={deliveryAddress.recepient_line_2 ?? ''}
                    error={errors.recepient_line_2}
                    highlight={highlightFields?.includes('recepient_line_2')}
                    placeholder="Complemento de dirección (opcional)"
                    onChange={handleChange}
                />
                <Field
                    name="recepient_postal_code"
                    label="Código postal"
                    value={deliveryAddress.recepient_postal_code}
                    error={errors.recepient_postal_code}
                    highlight={highlightFields?.includes('recepient_postal_code')}
                    placeholder="28001"
                    onChange={handleChange}
                />
                <Field
                    name="recepient_city"
                    label="Ciudad"
                    value={deliveryAddress.recepient_city}
                    error={errors.recepient_city}
                    highlight={highlightFields?.includes('recepient_city')}
                    placeholder="Madrid"
                    onChange={handleChange}
                />
                <Field
                    name="recepient_province"
                    label="Provincia"
                    value={deliveryAddress.recepient_province}
                    error={errors.recepient_province}
                    highlight={highlightFields?.includes('recepient_province')}
                    placeholder="Madrid"
                    onChange={handleChange}
                />
                {/* Country select */}
                {(() => {
                    const countryMarked =
                        !!errors.recepient_country ||
                        !!highlightFields?.includes('recepient_country');
                    const countryOptions = restrictToSpainIndividual
                        ? COUNTRY_OPTIONS.filter((o) => o.value === 'ES')
                        : COUNTRY_OPTIONS;
                    const selectedCountry =
                        countryOptions.find(
                            (o) => o.value === deliveryAddress.recepient_country
                        ) ?? null;
                    return (
                        <div className="space-y-1.5">
                            <label
                                htmlFor="recepient_country"
                                className={`block text-sm italic ${countryMarked ? 'text-red-600' : 'text-gray-700'}`}
                            >
                                País
                            </label>
                            <Select
                                inputId="recepient_country"
                                options={countryOptions}
                                value={selectedCountry}
                                isDisabled={restrictToSpainIndividual}
                                onChange={(option) => {
                                    const val = option?.value ?? '';
                                    setDeliveryAddress({
                                        ...deliveryAddress,
                                        recepient_country: val,
                                    });
                                    onClearHighlight?.('recepient_country');
                                    if (errors.recepient_country) {
                                        setErrors((prev) => ({
                                            ...prev,
                                            recepient_country: undefined,
                                        }));
                                    }
                                }}
                                placeholder="Selecciona un país..."
                                isSearchable
                                classNamePrefix="react-select"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        borderRadius: 0,
                                        minHeight: '40px',
                                        borderColor: countryMarked ? '#ef4444' : '#000',
                                        boxShadow: 'none',
                                        backgroundColor: restrictToSpainIndividual ? '#f9fafb' : base.backgroundColor,
                                        '&:hover': {
                                            borderColor: countryMarked ? '#ef4444' : '#000',
                                        },
                                    }),
                                    option: (base, state) => ({
                                        ...base,
                                        backgroundColor: state.isSelected
                                            ? '#F90000'
                                            : state.isFocused
                                            ? '#fee2e2'
                                            : 'white',
                                        color: state.isSelected ? 'white' : '#111',
                                        cursor: 'pointer',
                                    }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                            />
                            {restrictToSpainIndividual ? (
                                <p className="text-xs text-gray-500">
                                    Los envíos individuales solo están disponibles en España.
                                </p>
                            ) : (
                                errors.recepient_country && (
                                    <p id="recepient_country-error" className="text-red-500 text-xs mt-1">
                                        {errors.recepient_country}
                                    </p>
                                )
                            )}
                        </div>
                    );
                })()}
            </div>
        );
    }
);

export default DeliveryAddressForm;
